import {
  QUALITY_CONTROL_SECTION_TITLES,
  QualityControlOutput,
  QualityControlSection,
  getActiveFlagCountsBySection,
  schQualityControlSection,
} from '@aloft/toolkit-shared';
import { Button, Flex, ProductColor, Typography } from '@aloft/toolkit-sui';

import { useState } from 'react';

export const QualityControlReviewSidebar = ({
  qualityControl,
  qualityControlPassed,
  isRunningReview,
  activeSection,
  onSectionSelect,
  onRunComplianceCheck,
  onCertify,
}: {
  qualityControl: QualityControlOutput;
  qualityControlPassed: boolean;
  isRunningReview: boolean;
  activeSection: QualityControlSection;
  onSectionSelect: (section: QualityControlSection) => void;
  onRunComplianceCheck: () => void;
  onCertify: () => void;
}) => {
  const [hoveredSection, setHoveredSection] = useState<QualityControlSection | null>(null);
  const counts = getActiveFlagCountsBySection(qualityControl);
  const total = schQualityControlSection.options.reduce(
    (sum, section) => sum + (counts[section] ?? 0),
    0,
  );
  const hasPendingFlags = total > 0;

  return (
    <Flex
      flexDirection="column"
      height="100%"
      width="280px"
      padding="24px"
      boxSizing="border-box"
      backgroundColor="surface1"
      flexShrink={0}
      justifyContent="flex-start"
      borderRightWidth="1px"
      borderStyle="solid"
      borderColor="strokeDefault"
    >
      <Flex flexDirection="column" gap="64px" width="100%">
        <Flex flexDirection="column" gap="8px" width="100%">
          <Typography variant="subtitle2" color="textSecondary">
            AI REPORT REVIEW
          </Typography>
          <Typography variant="displayLarge" color="textPrimary">
            {total}
          </Typography>
          <Typography variant="body1" color="textSecondary">
            items to resolve before certification
          </Typography>
        </Flex>
        <Flex flexDirection="column" gap="16px" width="100%">
          {qualityControlPassed ? (
            <Button text="Certify report" variant="primary" fullWidth onClick={onCertify} />
          ) : (
            <>
              <Button
                text="Run compliance check"
                variant="primary"
                fullWidth
                disabled={hasPendingFlags}
                isLoading={isRunningReview}
                onClick={onRunComplianceCheck}
              />
              <Button text="Certify report" variant="secondary" fullWidth onClick={onCertify} />
            </>
          )}
        </Flex>
        <Flex flexDirection="column" gap="8px" width="100%">
          {schQualityControlSection.options.map((section) => {
            const isActive = activeSection === section;
            const isHovered = hoveredSection === section;
            const itemVariant = isActive ? 'h4' : 'body1';
            const itemColor = isActive ? 'buttonDefault' : undefined;
            let backgroundColor: ProductColor | undefined;
            if (isActive) {
              backgroundColor = 'selected4';
            } else if (isHovered) {
              backgroundColor = 'hover1';
            }
            return (
              <Flex
                key={section}
                justifyContent="space-between"
                alignItems="center"
                height="40px"
                paddingX="12px"
                paddingY="8px"
                width="100%"
                cursor="pointer"
                borderRadius="4px"
                overflow="hidden"
                backgroundColor={backgroundColor}
                __testId={`quality-control-review-nav-${section}`}
                data-selected={isActive}
                onMouseEnter={() => setHoveredSection(section)}
                onMouseLeave={() => setHoveredSection(null)}
                onClick={() => onSectionSelect(section)}
              >
                <Typography variant={itemVariant} color={itemColor}>
                  {QUALITY_CONTROL_SECTION_TITLES[section]}
                </Typography>
                <Typography variant={itemVariant} color={itemColor}>
                  {counts[section] ?? 0}
                </Typography>
              </Flex>
            );
          })}
        </Flex>
      </Flex>
    </Flex>
  );
};
